/**
 * Two equal columns on a 16px gutter: a photograph on one side, a card on the other,
 * with the card's content pinned top and bottom.
 *
 * This layout is not one section. `split-content-media` (`6377:87197`) and
 * `visa-feature` are separate entries in the catalogue that draw the same thing with
 * different copy, so the layout lives here once.
 *
 * It used to be four. `academy-banner` (`6377:88005`) and `tool-cta` were kept apart on
 * the argument that "an editor picking Academy banner knows what they are getting"; on
 * 2026-09-04 that lost to the argument that two entries drawing one layout is two places
 * to fix a bug. Both were merged away.
 *
 * `imageSide` flips the columns with `order` rather than by reordering the markup, so
 * the reading order stays photo-then-text for a screen reader and on a narrow viewport.
 */
import type { ReactNode } from 'react';
import type { MediaValue } from '../../types/sections';
import { SIZES } from '../../components/ui/Image';
import { MediaFrame, aspectClass, hasMedia } from '../../components/ui/MediaFrame';

export function MediaSplit({
  media, imageSide = 'right', variant = 'card', joined = false, cardTone = 'grey', children
}: {
  media?: MediaValue;
  imageSide?: 'left' | 'right';
  /**
   * `card` is the grey panel the home page uses (3883:70420). `plain` sets the same
   * content straight on the page background, indented from the column edge, which is how
   * Business Travel draws it (3883:87935) - no fill, no padding of its own.
   */
  variant?: 'card' | 'plain';
  /**
   * Photograph and card butted together with no gutter, the corners where they meet square
   * and only the pair's outer corners rounded (6373:16052). Only meaningful for the card
   * variant with a photograph; `visa-feature` keeps its two separate tiles.
   */
  joined?: boolean;
  /** The card's fill. Grey is the default, which is what `visa-feature` has always had; `split-content-media` asks for white, the design's (6373:16052). */
  cardTone?: 'white' | 'grey' | 'blue';
  children: ReactNode;
}) {
  const imageLeft = imageSide === 'left';
  const image = hasMedia(media);
  // The photograph's own proportion decides the row's height when the editor set one.
  // Without it the row falls back to the 401px floor the home page measures.
  const aspect = aspectClass(media);
  const fill = { white: 'bg-surface', grey: 'bg-surface-muted', blue: 'bg-blue-50' }[cardTone] ?? 'bg-surface-muted';
  const join = joined && image && variant === 'card';
  // One value per corner, written out: `rounded-l` is a token here, not "the left side".
  // Literal strings, because Tailwind reads class names from the source and cannot see ones
  // assembled from a variable.
  const figureCorners = !join ? '' : imageLeft
    ? '[border-radius:14px_14px_0_0] lg:[border-radius:14px_0_0_14px]'
    : '[border-radius:14px_14px_0_0] lg:[border-radius:0_14px_14px_0]';
  const cardCorners = !join ? 'rounded-l' : imageLeft
    ? '[border-radius:0_0_14px_14px] lg:[border-radius:0_14px_14px_0]'
    : '[border-radius:0_0_14px_14px] lg:[border-radius:14px_0_0_14px]';

  return (
    <div className={`grid lg:grid-cols-2 ${join ? 'gap-0' : 'gap-4'}`}>
      {image ? (
        // `absolute inset-0` and not `h-full` on the image itself: `h-full` against an
        // auto-height figure resolves to the image's own aspect ratio, so a square
        // photograph made the row 672px tall instead of the design's 401.
        <figure
          className={`relative m-0 overflow-hidden ${join ? figureCorners : 'rounded-l'} bg-surface-sunken ${
            aspect ?? 'min-h-[401px]'
          } ${imageLeft ? 'lg:order-first' : 'lg:order-last'}`}
        >
          <div className="absolute inset-0">
            <MediaFrame media={media} sizes={SIZES.half} />
          </div>
        </figure>
      ) : null}

      {/* The card matches the photograph rather than setting its own floor: a grid item
          already stretches to the row, so a `min-h` here would only ever fight it. */}
      <div
        className={`flex flex-col justify-center gap-10 ${aspect && image ? '' : 'min-h-[401px]'} ${
          variant === 'plain' ? 'lg:px-14' : `justify-between ${cardCorners} ${fill} p-10`
        } ${image ? '' : 'lg:col-span-2'}`}
      >
        {children}
      </div>
    </div>
  );
}
